
























































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash";
import { Group, InputGroupMembersUpdate } from "@scrile/api-provider/dist/api/GroupsProvider";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseInput from "@/components/BaseInput";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner";
import CreateGroupModal from "@/components/CreateGroupModal";
import EmptyContent from "@/components/EmptyContent";
import GroupDeleteConfirmation from "@/components/GroupDeleteConfirmation";
import GroupMenu from "@/components/GroupMenu";
import InfiniteScroll from "@/components/InfiniteScroll";
import PageTitle from "@/components/PageTitle";
import GroupUserItem from "./components/GroupUserItem.vue";
import { UserWithExistInGroup } from "./types";

const USER_FRAGMENT: FragmentFields<UserPublicData> = [
  "id",
  "screenName",
  "username",
  { onlineStatus: ["isOnline"] },
  { avatar: ["urlPart", "uuid", "updateTime", "metadata"] },
  { subscriptionToMe: ["amount", { recurringBillingSchedule: ["cancelled"] }] },
];

export default defineComponent({
  name: "PageGroupUsers",
  components: {
    GroupDeleteConfirmation,
    CreateGroupModal,
    BasePage,
    PageTitle,
    GroupMenu,
    BaseSpinner,
    BaseIcon,
    BaseInput,
    GroupUserItem,
    InfiniteScroll,
    EmptyContent,
  },
  props: {
    groupId: { type: String, required: true },
  },
  setup(props) {
    const { authUserStrict } = useAuthUser();
    const loading = ref(false);
    const group = ref<Group | null>(null);
    const term = ref("");
    const filterLoading = ref(false);
    const showCreateModal = ref(false);
    const showDeleteModal = ref(false);
    const searchUsers = ref<UserWithExistInGroup[]>([]);
    const showList = computed(() => {
      return term.value.length > 1 && searchUsers.value.length > 0;
    });

    const isMemberCurrentlySubscribed = (member: UserPublicData) => {
      return member.subscriptionToMe?.recurringBillingSchedule?.cancelled === false;
    };

    const getGroupData = async () => {
      const response = await providers.GroupsProvider.search(
        {
          data: { id: props.groupId, ownerId: authUserStrict.value.id },
        },
        ["id", "name", "system", "ownerId"]
      );
      group.value = response.find(i => i.id === props.groupId) ?? null;
    };

    const { items, enableScroll, getFirstPage, getNextPage, removeItem } = usePager(page => {
      return providers.GroupsProvider.searchMembers(
        {
          data: { groupId: props.groupId, system: group.value!.system, page },
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ["groupId", "userId", { user: USER_FRAGMENT }]
      );
    });

    const groupMembers = computed(() => items.value.map(i => ({ ...(i.user as UserPublicData), existInGroup: true })));

    const membersList = computed(() => {
      if (showList.value) {
        return searchUsers.value;
      }
      return groupMembers.value;
    });

    const getSearchUsers = debounce(async (stringAfterAt: string) => {
      if (!group.value || group.value.system) return;
      try {
        const users = await providers.UserPublicDataProvider.relevantSearchByName(
          {
            name: stringAfterAt,
          },
          USER_FRAGMENT
        );
        const response = await providers.GroupsProvider.searchMembers({
          data: { groupId: props.groupId, system: group.value.system, userIds: users.map(i => i.id) },
        });
        searchUsers.value = users.map(i => ({
          ...i,
          existInGroup: response.result.some(r => r.userId === i.id),
        }));
      } finally {
        filterLoading.value = false;
      }
    }, 500);

    const setTerm = async (termValue: string) => {
      term.value = termValue;
      filterLoading.value = true;
      if (term.value.length > 1) {
        await getSearchUsers(term.value);
      } else {
        getSearchUsers.cancel();
        filterLoading.value = false;
      }
    };

    const onUpdateUser = async (data: InputGroupMembersUpdate, callback: () => void) => {
      try {
        await providers.GroupsProvider.updateMembers({ data: [data] });
        searchUsers.value = searchUsers.value.map(i =>
          i.id === data.memberId ? { ...i, existInGroup: data.status } : i
        );
        if (!showList.value && !data.status) {
          await removeItem(i => i.userId === data.memberId);
        }
      } finally {
        callback();
      }
    };

    const getData = async () => {
      try {
        loading.value = true;
        await getGroupData();
        if (group.value) {
          await getFirstPage();
        }
      } finally {
        loading.value = false;
      }
    };

    getData();

    watch(showList, async (value, oldValue) => {
      if (oldValue && !value) {
        await getFirstPage();
      }
    });

    return {
      loading,
      getData,
      group,
      membersList,
      enableScroll,
      term,
      filterLoading,
      showCreateModal,
      showDeleteModal,
      setTerm,
      isMemberCurrentlySubscribed,
      onUpdateUser,
      getNextPage,
    };
  },
});
